import * as React from 'react';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ArtikelDetailLieferant, useArtikelDetailLieferantList } from 'api/artikelDetailLieferant';
import { einheitenByCodeSelector } from 'store/lookups/selector';
import { useSelector } from 'react-redux';
import { formatMoney, formatPercent, formatSmDate, formatSmDateTime } from 'utils/formatter';
import classnames from 'classnames';
import { formStyles } from 'styles/formStyles';
import { ArtikelDetailKalkulation, useArtikelDetailKalkulationList } from 'api/artikelDetailKalkulation';
import { DialogFooter, PrimaryButton } from '@fluentui/react';
import { useBetreuerByKz } from 'api/betreuer';
import { EditIcon } from '@fluentui/react-icons';
import useArtikelCalculator from 'hooks/useArtikelCalculator';
import { defaultArtikel, useArtikel } from 'api/artikel';
import { ArtikelDetailLieferantEdit } from './ArtikelDetailLieferantEdit';
import { ArtikelDetailKalkulationNew } from './ArtikelDetailKalkulationNew';

const theme: ITheme = getTheme();
const { palette } = theme;

const styles = mergeStyleSets({
    container: {},
    lieferantListContainer: {
        height: 150,
        overflowY: 'scroll',
    },
    lieferantListContainerEmbedded: {},
    lieferantListRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 30px 100px 80px 100px 80px 100px 80px 20px',
        gridColumnGap: 10,
        marginTop: 10,
    },
    kalkulationListContainer: {
        height: 324,
        overflowY: 'scroll',
    },
    kalkulationListContainerEmbedded: {},
    kalkulationListRow: {
        display: 'grid',
        gridTemplateColumns: '80px 60px 80px 100px 80px 100px 100px 80px 100px 1fr',
        gridColumnGap: 10,
        marginTop: 10,
    },
    rowHeader: {
        fontWeight: 700,
    },
    selectableRow: {
        cursor: 'pointer',
        selectors: {
            '&:hover': { backgroundColor: palette.neutralLight },
        },
    },
    editIconCell: {
        marginRight: 5,
    },
    obsoleteRow: {
        color: '#888',
    },
    decimalValue: {
        textAlign: 'right',
    },
});

interface ArtikelDetailLieferantEditData {
    artikelGruppeNr: number;
    artikelNr: string;
    lNr: number;
}

interface ArtikelDetailKalkulationProps {
    artikelGruppeNr: number;
    artikelNr: string;
    embedded?: boolean;
}

export const ArtikelDetailKalkulationen: React.FC<ArtikelDetailKalkulationProps> = ({
    artikelGruppeNr,
    artikelNr,
    embedded,
}) => {
    const einheitenByCode = useSelector(einheitenByCodeSelector);
    const betreuerByKz = useBetreuerByKz();
    const [showKalkulationNew, setShowKalkulationNew] = React.useState(false);

    const artikel = useArtikel({
        artikelNr,
        includeArchiv: true,
    });

    const artikelDetailLieferantList = useArtikelDetailLieferantList({
        artikelGruppeNr,
        artikelNr,
        withLieferant: true,
    });

    const sortedArtikelDetailLieferantList = React.useMemo<ArtikelDetailLieferant[]>(() => {
        return (artikelDetailLieferantList.data || []).sort((a, b) => b.vkGiltAb.localeCompare(a.vkGiltAb));
    }, [artikelDetailLieferantList.data]);

    const [
        selectedArtikelDetailLieferant,
        setSelectedArtikelDetailLieferant,
    ] = React.useState<ArtikelDetailLieferant | null>(null);

    const [
        artikelDetailLieferantEditData,
        setArtikelDetailLieferantEditData,
    ] = React.useState<ArtikelDetailLieferantEditData | null>(null);

    React.useEffect(() => {
        if (sortedArtikelDetailLieferantList?.length > 0) {
            setSelectedArtikelDetailLieferant(sortedArtikelDetailLieferantList[0]);
        }
    }, [sortedArtikelDetailLieferantList]);

    const artikelDetailKalkulationList = useArtikelDetailKalkulationList(
        {
            artikelGruppeNr: selectedArtikelDetailLieferant?.artikelGruppeNr || 0,
            artikelNr: selectedArtikelDetailLieferant?.artikelNr || '',
            lNr: selectedArtikelDetailLieferant?.lNr || undefined,
            onlyHauptlieferant: false,
            onlyLatest: false,
        },
        {
            enabled: !!selectedArtikelDetailLieferant,
        },
    );

    const sortedArtikelDetailKalkulationList = React.useMemo<ArtikelDetailKalkulation[]>(() => {
        return (artikelDetailKalkulationList.data || []).sort((a, b) =>
            a.datum === b.datum ? a.lfnr - b.lfnr : b.datum.localeCompare(a.datum),
        );
    }, [artikelDetailKalkulationList.data]);

    const artikelCalculator = useArtikelCalculator();

    const firstDatum = sortedArtikelDetailKalkulationList[0]?.datum;

    return (
        <>
            <div className={styles.container}>
                <div className={classnames(styles.lieferantListRow, styles.rowHeader)}>
                    <div>Lieferant</div>
                    <div>Artikel-Nr. - Lieferant</div>
                    <div>HL</div>
                    <div>Preisgruppe</div>
                    <div>Einheit</div>
                    <div className={formStyles.gridCellRight}>EK-Preis</div>
                    <div>EKP gilt ab</div>
                    <div className={formStyles.gridCellRight}>VK-Preis</div>
                    <div>VKP gilt ab</div>
                    <div />
                </div>
                <div className={embedded ? styles.lieferantListContainerEmbedded : styles.lieferantListContainer}>
                    {sortedArtikelDetailLieferantList.map((artikelDetailLieferant, index) => {
                        const lieferantLabel = artikelDetailLieferant.lieferant
                            ? (artikelDetailLieferant.lieferant.name1 || '') +
                              ' ' +
                              (artikelDetailLieferant.lieferant.name2 || '') +
                              (artikelDetailLieferant.lieferant.nr ? `(${artikelDetailLieferant.lieferant.nr})` : '')
                            : '';

                        return (
                            <div
                                key={artikelDetailLieferant.lNr}
                                className={classnames(styles.lieferantListRow, styles.selectableRow, {
                                    [formStyles.listItemSelected]:
                                        selectedArtikelDetailLieferant?.lNr === artikelDetailLieferant.lNr,
                                    [styles.obsoleteRow]: index > 0,
                                })}
                                onClick={() => {
                                    setSelectedArtikelDetailLieferant(artikelDetailLieferant);
                                }}
                            >
                                <div className={formStyles.gridCell}>{lieferantLabel}</div>
                                <div className={formStyles.gridCell}>{artikelDetailLieferant.lieferantArtikelNr}</div>
                                <div className={formStyles.gridCell}>{artikelDetailLieferant.art ? 'X' : ''}</div>
                                <div className={formStyles.gridCell}>{artikelDetailLieferant.kalkPreisGrp}</div>
                                <div className={formStyles.gridCell}>
                                    {artikelDetailLieferant.einheit
                                        ? einheitenByCode[artikelDetailLieferant.einheit].text ||
                                          artikelDetailLieferant.einheit
                                        : ''}
                                </div>
                                <div className={formStyles.gridCellRight}>
                                    {formatMoney(artikelDetailLieferant.ekPreis, 3)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {formatSmDate(artikelDetailLieferant.ekGiltAb)}
                                </div>
                                <div className={formStyles.gridCellRight}>
                                    {formatMoney(artikelDetailLieferant.vkPreis, 3)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {formatSmDate(artikelDetailLieferant.vkGiltAb)}
                                </div>
                                <div className={styles.editIconCell}>
                                    {!embedded && (
                                        <EditIcon
                                            className={formStyles.gridCell}
                                            onClick={() =>
                                                setArtikelDetailLieferantEditData({
                                                    artikelGruppeNr: artikelDetailLieferant.artikelGruppeNr,
                                                    artikelNr: artikelDetailLieferant.artikelNr,
                                                    lNr: artikelDetailLieferant.lNr,
                                                })
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className={classnames(styles.kalkulationListRow, styles.rowHeader)}>
                    <div>Kalk.-Dat.</div>
                    <div>Lfnd.Nr.</div>
                    <div>EK1 Einh.</div>
                    <div className={formStyles.gridCellRight}>EK1</div>
                    <div>Einheit</div>
                    <div className={formStyles.gridCellRight}>EK1/Einheit</div>
                    <div className={formStyles.gridCellRight}>EK2/Einheit</div>
                    <div className={formStyles.gridCellRight}>Aufschlag</div>
                    <div className={formStyles.gridCellRight}>VK</div>
                    <div>Kalkuliert</div>
                </div>
                <div className={embedded ? styles.kalkulationListContainerEmbedded : styles.kalkulationListContainer}>
                    {sortedArtikelDetailKalkulationList.map((artikelDetailKalkulation) => {
                        const { value: artikelEhEk1 } = artikelCalculator.convertPreisByEinheit(
                            artikel?.data || defaultArtikel,
                            artikelDetailKalkulation.ek1,
                            artikelDetailKalkulation.ek1Eh,
                            artikel?.data?.einheit,
                        );

                        const { value: ek1ProEh } = artikelCalculator.convertPreisByEinheit(
                            artikel?.data || defaultArtikel,
                            artikelEhEk1,
                            artikel?.data?.einheit,
                            artikelDetailKalkulation.eh,
                        );

                        return (
                            <div
                                key={artikelDetailKalkulation.datum + '|' + artikelDetailKalkulation.lfnr}
                                className={classnames(styles.kalkulationListRow, {
                                    [styles.obsoleteRow]: firstDatum !== artikelDetailKalkulation.datum,
                                })}
                            >
                                <div className={formStyles.gridCell}>
                                    {formatSmDate(artikelDetailKalkulation.datum)}
                                </div>
                                <div className={formStyles.gridCell}>{artikelDetailKalkulation.lfnr}</div>
                                <div className={formStyles.gridCell}>
                                    {artikelDetailKalkulation.ek1Eh
                                        ? einheitenByCode[artikelDetailKalkulation.ek1Eh].text ||
                                          artikelDetailKalkulation.ek1Eh
                                        : ''}
                                </div>
                                <div className={formStyles.gridCellRight}>
                                    {formatMoney(artikelDetailKalkulation.ek1, 3)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {artikelDetailKalkulation.eh
                                        ? einheitenByCode[artikelDetailKalkulation.eh].text ||
                                          artikelDetailKalkulation.eh
                                        : ''}
                                </div>
                                <div className={formStyles.gridCellRight}>{formatMoney(ek1ProEh, 3)}</div>
                                <div className={formStyles.gridCellRight}>
                                    {formatMoney(artikelDetailKalkulation.ek2, 3)}
                                </div>
                                <div className={formStyles.gridCellRight}>
                                    {formatPercent(artikelDetailKalkulation.vkProz, 2)}
                                </div>
                                <div className={formStyles.gridCellRight}>
                                    {formatMoney(artikelDetailKalkulation.vk, 3)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {artikelDetailKalkulation.updUser &&
                                        (betreuerByKz[artikelDetailKalkulation.updUser]
                                            ? betreuerByKz[artikelDetailKalkulation.updUser].name
                                            : artikelDetailKalkulation.updUser)}
                                    {' - '}
                                    {formatSmDateTime(artikelDetailKalkulation.updDat, artikelDetailKalkulation.updUhr)}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {!embedded && (
                    <DialogFooter>
                        <PrimaryButton
                            text="Neuer Lieferant"
                            onClick={() =>
                                setArtikelDetailLieferantEditData({
                                    artikelGruppeNr,
                                    artikelNr,
                                    lNr: -1,
                                })
                            }
                        />
                        <PrimaryButton
                            text="Neukalkulation"
                            disabled={!sortedArtikelDetailLieferantList.length}
                            onClick={() => setShowKalkulationNew(true)}
                        />
                    </DialogFooter>
                )}
            </div>
            {artikelDetailLieferantEditData && (
                <ArtikelDetailLieferantEdit
                    artikelGruppeNr={artikelDetailLieferantEditData.artikelGruppeNr}
                    artikelNr={artikelDetailLieferantEditData.artikelNr}
                    lNr={artikelDetailLieferantEditData.lNr}
                    onClose={(refetch) => {
                        if (refetch) {
                            artikelDetailLieferantList.refetch().then();
                        }
                        setArtikelDetailLieferantEditData(null);
                    }}
                />
            )}
            {showKalkulationNew && selectedArtikelDetailLieferant && (
                <ArtikelDetailKalkulationNew
                    artikelGruppeNr={artikelGruppeNr}
                    artikelNr={artikelNr}
                    lNr={selectedArtikelDetailLieferant.lNr}
                    onClose={(refetch) => {
                        if (refetch) {
                            artikelDetailLieferantList.refetch();
                            artikelDetailKalkulationList.refetch();
                        }
                        setShowKalkulationNew(false);
                    }}
                />
            )}
        </>
    );
};
